/**
 * Zustand des Rezept-Editors: laden, ändern, speichern.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, isOffline } from '../api/client.ts';
import { enqueue } from './outbox.ts';
import type { MediaItem, RecipeDetail, RecipeDraft } from '../api/types.ts';
import type { ParsedIngredient } from '../lib/ingredientParser.ts';

/** Ein leerer Entwurf. */
export function emptyDraft(): RecipeDraft {
  return {
    title: '',
    description: '',
    difficulty: 'easy',
    baseAmount: 4,
    baseUnit: 'portions',
    baseUnitDetail: '',
    times: {
      preparationMinutes: 0,
      activeMinutes: 0,
      cookingMinutes: 0,
      bakingMinutes: 0,
      restingMinutes: 0,
      coolingMinutes: 0,
    },
    equipment: [],
    categoryIds: [],
    tagNames: [],
    ingredients: [],
    steps: [],
  };
}

/** Wandelt ein geladenes Rezept in einen Entwurf um. */
export function draftFromRecipe(recipe: RecipeDetail): RecipeDraft {
  return {
    title: recipe.title,
    description: recipe.description ?? '',
    difficulty: recipe.difficulty,
    baseAmount: recipe.baseAmount,
    baseUnit: recipe.baseUnit,
    baseUnitDetail: recipe.baseUnitDetail ?? '',
    times: { ...recipe.times },
    equipment: [...recipe.equipment],
    categoryIds: [...recipe.categoryIds],
    tagNames: [...recipe.tagNames],
    ingredients: recipe.ingredients.map((ingredient) => ({
      id: ingredient.id,
      group: ingredient.group ?? '',
      amount: ingredient.amount,
      unit: ingredient.unit,
      name: ingredient.name,
      note: ingredient.note ?? '',
      scaling: ingredient.scaling,
    })),
    steps: recipe.steps.map((step) => ({
      id: step.id,
      section: step.section ?? '',
      title: step.title,
      instruction: step.instruction,
      durationMinutes: step.durationMinutes,
      passive: step.passive,
      parallel: step.parallel,
      timer: step.timer,
      temperatureCelsius: step.temperatureCelsius,
      // Ohne diese beiden Felder gingen gewählte Animationen beim Speichern verloren.
      animationTemplateId: step.animationTemplateId,
      animationParams: { ...step.animationParams },
      whyItWorks: step.whyItWorks ?? '',
      doneWhen: step.doneWhen ?? '',
      // Im Gerät gespeicherte ältere Fassungen kennen noch keine Schrittmedien.
      media: [...(step.media ?? [])],
    })),
  };
}

/**
 * Wandelt den Entwurf in die Form, die der Server erwartet: Von den eigenen
 * Medien der Schritte gehen nur die Kennungen mit.
 */
export function toRecipeInput(draft: RecipeDraft) {
  return {
    ...draft,
    steps: draft.steps.map(({ media, ...step }) => ({ ...step, mediaIds: media.map((item) => item.id) })),
  };
}

/** Ergebnis des Editor-Zustands. */
export interface EditorState {
  readonly draft: RecipeDraft;
  /** Bilder und Videos des Rezepts. */
  readonly media: MediaItem[];
  readonly loading: boolean;
  readonly saving: boolean;
  readonly error: string | null;
  setDraft: (draft: RecipeDraft) => void;
  setMedia: (media: MediaItem[]) => void;
  addIngredients: (parsed: readonly ParsedIngredient[]) => void;
  addStep: () => void;
  save: (force?: boolean) => Promise<SaveResult>;
  /** Neuere Fassung vom Server, falls beim Speichern ein Konflikt auftrat. */
  readonly conflict: RecipeDetail | null;
  /** Verwirft die eigenen Änderungen und lädt die neuere Fassung. */
  takeTheirs: () => void;
}

/** Ergebnis des Speicherns. */
export type SaveResult =
  | { readonly kind: 'saved'; readonly id: string }
  /** Ohne Netz in die Warteschlange gelegt; id ist bei neuen Rezepten noch unbekannt. */
  | { readonly kind: 'queued'; readonly id: string | null }
  | { readonly kind: 'conflict' }
  | { readonly kind: 'error' };

/**
 * Hält den Entwurf eines Rezepts.
 * @param recipeId Kennung beim Bearbeiten, null beim Anlegen.
 */
export function useRecipeEditor(recipeId: string | null): EditorState {
  const [draft, setDraft] = useState<RecipeDraft>(emptyDraft);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(recipeId !== null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stand des Rezepts, auf dem die Bearbeitung beruht – für die Konfliktprüfung.
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(null);
  const [conflict, setConflict] = useState<RecipeDetail | null>(null);

  useEffect(() => {
    if (recipeId === null) {
      setDraft(emptyDraft());
      setMedia([]);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    api
      .get<{ recipe: RecipeDetail }>(`/api/recipes/${recipeId}`)
      .then((result) => {
        if (alive) {
          setDraft(draftFromRecipe(result.recipe));
          setMedia([...result.recipe.media]);
          setBaseUpdatedAt(result.recipe.updatedAt);
          setLoading(false);
        }
      })
      .catch((caught: unknown) => {
        if (alive) {
          setError(caught instanceof ApiError ? caught.message : 'Das Rezept konnte nicht geladen werden.');
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [recipeId]);

  /** Hängt erkannte Zutaten an. */
  const addIngredients = useCallback((parsed: readonly ParsedIngredient[]) => {
    setDraft((current) => ({
      ...current,
      ingredients: [
        ...current.ingredients,
        ...parsed.map((entry) => ({
          // Feste Kennung schon im Editor, damit sich neue Zeilen verschieben lassen.
          id: crypto.randomUUID(),
          group: '',
          amount: entry.amount,
          unit: entry.unit,
          name: entry.name,
          note: entry.note,
          scaling: entry.scaling,
        })),
      ],
    }));
  }, []);

  /** Fügt einen leeren Schritt hinzu. */
  const addStep = useCallback(() => {
    setDraft((current) => ({
      ...current,
      steps: [
        ...current.steps,
        {
          id: crypto.randomUUID(),
          section: '',
          title: '',
          instruction: '',
          durationMinutes: null,
          passive: false,
          parallel: false,
          timer: false,
          temperatureCelsius: null,
          animationTemplateId: null,
          animationParams: {},
          whyItWorks: '',
          doneWhen: '',
          media: [],
        },
      ],
    }));
  }, []);

  /** Speichert den Entwurf und liefert die Kennung des Rezepts. */
  const save = useCallback(
    async (force = false): Promise<SaveResult> => {
      setSaving(true);
      setError(null);
      const title = draft.title.trim() === '' ? 'Rezept' : draft.title.trim();
      const input = toRecipeInput(draft);
      try {
        if (recipeId === null) {
          const result = await api.post<{ recipe: RecipeDetail }>('/api/recipes', input);
          return { kind: 'saved', id: result.recipe.id };
        }
        const body = force || baseUpdatedAt === null ? input : { ...input, baseUpdatedAt };
        const result = await api.put<{ recipe: RecipeDetail }>(`/api/recipes/${recipeId}`, body);
        setBaseUpdatedAt(result.recipe.updatedAt);
        setConflict(null);
        return { kind: 'saved', id: result.recipe.id };
      } catch (caught) {
        if (isOffline(caught)) {
          // Ohne Netz: merken und später übertragen. Ein neues Rezept bekommt
          // seine Kennung erst vom Server.
          enqueue(
            recipeId === null
              ? {
                  key: `new-recipe:${crypto.randomUUID()}`,
                  method: 'POST',
                  path: '/api/recipes',
                  body: { ...input },
                  label: `Neues Rezept „${title}“ anlegen`,
                }
              : {
                  key: `recipe:${recipeId}`,
                  method: 'PUT',
                  path: `/api/recipes/${recipeId}`,
                  body: { ...input, ...(baseUpdatedAt === null || force ? {} : { baseUpdatedAt }) },
                  label: `Rezept „${title}“ speichern`,
                },
          );
          return { kind: 'queued', id: recipeId };
        }
        if (caught instanceof ApiError && caught.status === 409) {
          const current = (caught.data as { recipe?: RecipeDetail } | null)?.recipe ?? null;
          setConflict(current);
          return { kind: 'conflict' };
        }
        setError(caught instanceof ApiError ? caught.message : 'Das Speichern ist fehlgeschlagen.');
        return { kind: 'error' };
      } finally {
        setSaving(false);
      }
    },
    [draft, recipeId, baseUpdatedAt],
  );

  /** Übernimmt die neuere Fassung des anderen Geräts. */
  const takeTheirs = useCallback(() => {
    if (conflict === null) {
      return;
    }
    setDraft(draftFromRecipe(conflict));
    setMedia([...conflict.media]);
    setBaseUpdatedAt(conflict.updatedAt);
    setConflict(null);
  }, [conflict]);

  return {
    draft,
    media,
    loading,
    saving,
    error,
    setDraft,
    setMedia,
    addIngredients,
    addStep,
    save,
    conflict,
    takeTheirs,
  };
}
